/* eslint-disable react/prop-types */
import { WidgetCommonHorizontalContainer } from "@/components/widgets/WidgetCommonContainer"
import { WidgetWrapper } from "./WidgetWrapper"
import LeftIcon from "@/assets/clipart/widgets/preapproval-letter-graphic.svg"

import * as HomeEvents from "@/analytics/events/home"
import {
  preApprovalView,
  requestPreApprovalConfirm,
} from "@/analytics/events/home"
import { useCallback, useEffect, useRef } from "react"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
} from "@gorhom/bottom-sheet"
import { BFColors } from "@/constants/Colors"
import { View } from "react-native"
import FeedbackTextInput from "@/components/input/FeedbackTextInput"
import { useUser } from "@/queries/user"
import { usePhoneNumberInput } from "@/hooks/usePhoneNumberInput"
import {
  PrimaryButton,
  PrimaryDarkButton,
} from "@/components/buttons/CommonButtons"
import type { AppScreen } from "@/constants/Screens"
import {
  $preApprovalHasUpdated,
  setPreApprovalUpdated,
} from "@/stores/preapproval"
import { useStore } from "@nanostores/react"
import Spacer from "@/components/util/Spacer"
import ThumbsUpAnimated from "@/components/widgets/ThumbsUpAnimated"
import BFText from "@/components/standard/BFText"
import { AddressDisplay } from "@/components/AddressDisplay"
import EmptyView from "@/components/EmptyView"
import { openNewLoanQuote } from "@/screens/loans/utils/openNewLoanQuote"

export default function PreApprovalWidget() {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const titleText = `Pre-Approval Letter`
  const ctaText = `Strengthen your offer immediately!`
  // To test.... TODO: Eventually persist value to either display widget or not
  const hasRequestedLetter: boolean = false

  const onPress = () => {
    HomeEvents.homepageWidgetClicked({
      nextScreen: "PreApproval Letter Modal",
      widgetType: "widget-preapproval",
      widgetState: hasRequestedLetter ? "non-zero-value" : "zero-value",
      widgetTitle: titleText,
      widgetCta: ctaText,
    })

    preApprovalView("Home")

    bottomSheetModalRef.current?.present()
  }

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
      />
    ),
    []
  )

  if (hasRequestedLetter) {
    return null
  }

  return (
    <WidgetWrapper
      category="preapproval"
      orientation="horizontal"
      title={titleText}
      onPress={onPress}
    >
      <WidgetCommonHorizontalContainer
        IconComponent={LeftIcon}
        ImageComponent={EmptyView}
        subTitle={titleText}
        ctaText={ctaText}
        layoutType={"short"}
      />

      <BottomSheetModal
        ref={bottomSheetModalRef}
        snapPoints={["85%"]}
        backdropComponent={renderBackdrop}
        index={0}
        backgroundStyle={{ backgroundColor: BFColors.calmBlue }}
        handleIndicatorStyle={{ backgroundColor: "#FFFFFF" }}
        enableDynamicSizing={false}
      >
        <PreApprovalPopover
          fromScreen={"Home"}
          onStart={() => {
            openNewLoanQuote()
            bottomSheetModalRef?.current?.dismiss()
          }}
          onDismiss={() => {
            bottomSheetModalRef?.current?.dismiss()
          }}
        />
      </BottomSheetModal>
    </WidgetWrapper>
  )
}

interface PreApprovalPopoverProps {
  fromScreen: AppScreen
  onStart: () => void
  onDismiss: () => void
  propertyId?: string
  addressLine1?: string
  addressLine2?: string
}

export const PreApprovalPopover: React.FC<PreApprovalPopoverProps> = ({
  fromScreen,
  onStart,
  propertyId,
  addressLine1,
  addressLine2,
}: PreApprovalPopoverProps) => {
  const { data, isLoading } = useUser()
  const preApprovalState = useStore($preApprovalHasUpdated)

  const onPreApprovalRequestSuccess = useCallback(() => {
    setPreApprovalUpdated(true)
  }, [])

  const {
    phoneNumber,
    onPhoneChange,
    onPreApprovalRequest,
    isPending,
    validatePhoneNumber,
    isValid,
  } = usePhoneNumberInput(data?.me?.phoneNumber, onPreApprovalRequestSuccess)

  useEffect(() => {
    if (!isLoading && data?.me?.phoneNumber) {
      validatePhoneNumber()
    }
  }, [isLoading, data?.me?.phoneNumber, validatePhoneNumber])

  const onConfirm = () => {
    if (validatePhoneNumber()) {
      requestPreApprovalConfirm()
      onPreApprovalRequest(propertyId || null)
    }
  }

  if (!data) {
    // Check if user.me is available
    return null // or some loading spinner or placeholder component
  }

  if (preApprovalState) {
    return (
      <View className="flex-col justify-start flex-1">
        {fromScreen === "Subject Property" ? (
          <AddressDisplay
            street={addressLine1 || ""}
            cityStateZip={addressLine2 || ""}
          />
        ) : (
          <View style={{ marginTop: 98 }}></View>
        )}
        <BFText className="text-center h-12 text-teal text-[26px] font-extrabold font-figtree-extrabold leading-7">
          Great!
        </BFText>
        <BFText className="text-center text-[16px] text-teal font-extrabold font-figtree-extrabold leading-tight tracking-tight">
          {`To finalize your pre-approval letter\nyou’ll need to submit a non-binding\nloan application`}
        </BFText>

        <View className="justify-center items-center mt-14">
          <ThumbsUpAnimated />
        </View>

        <Spacer />

        <View className="flex flex-col flex-1 items-center pb-[50px]">
          <PrimaryButton
            testID={"get-started-button"}
            title="Get Started"
            onPress={onStart}
            size={"lg"}
            disabled={isPending}
            style={{
              width: 330,
            }}
          />
          <BFText className="w-[330px] px-6 py-2 justify-center items-center text-white text-xs font-spacegrotesk tracking-tight">
            {`Applications are always non-binding and will never initiate\na credit review.`}
          </BFText>
        </View>
      </View>
    )
  }

  if (isLoading || !data?.me) {
    return // Or another loading indicator
  }
  const isDisabled = isLoading || isPending || !isValid
  const phoneWasProvided: boolean = !!data.me.phoneNumber?.length
  const isStoredNumber: boolean =
    phoneWasProvided && phoneNumber === data.me.phoneNumber
  const ctaText: string = isStoredNumber ? "Confirm" : "Call Me"

  return (
    <View className="flex-col justify-center items-center">
      <View>
        {fromScreen === "Subject Property" ? (
          <AddressDisplay
            street={addressLine1 || ""}
            cityStateZip={addressLine2 || ""}
          />
        ) : (
          <View style={{ marginTop: 42 }}></View>
        )}
        <BFText className="w-full text-teal text-[17px] font-figtree-extrabold">
          {phoneWasProvided
            ? `Confirm your number to receive a call\nfrom our lending experts`
            : `Provide your number to receive a call\nfrom our lending experts`}
        </BFText>
        <View className="pt-5 w-full h-[75px]">
          <FeedbackTextInput
            placeholder={"Phone Number"}
            className="w-[330px] border-white"
            value={phoneNumber || ""}
            onChange={onPhoneChange}
            disabled={false}
            autoFocus={true}
            keyboardType="phone-pad"
          />
        </View>
        <BFText className="w-[330px] text-white text-xs font-spacegrotesk tracking-tight ">
          {`By clicking “${ctaText}”, you agree that the pre-approval letter is non-binding and any loan will be subject to further underwriting`}
        </BFText>

        <View className="flex flex-col flex-1 items-center pt-[60px]">
          <PrimaryDarkButton
            testID={"CTAButton"}
            style={{ width: 330 }}
            title={ctaText}
            onPress={onConfirm}
            disabled={isDisabled}
          />
        </View>
      </View>
    </View>
  )
}
